/**
 * React Native WebView 브리지 유틸
 *
 * Vue 앱에서 RN 앱으로 메시지를 보내는 단일 진입점.
 * window.ReactNativeWebView 가 없는 웹 브라우저 환경에서는 자동으로 무시됨.
 */

type RNTab = 'Home' | 'Clubs' | 'MyPage' | 'Menu';

/** RN 하단 탭 전환 요청 */
export const switchRNTab = (tab: RNTab) => {
  if (window.ReactNativeWebView) {
    window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'SWITCH_TAB', tab }));
  }
};

/**
 * SNS OAuth 로그인을 RN 인앱 브라우저 시트(expo-web-browser)로 열도록 위임.
 * WebView 안 OAuth(구글·카카오 차단)를 피하고, Safari/Chrome 완전 이탈도 하지 않는다.
 * 인증 끝나면 RN이 콜백 URL을 다시 WebView에 올려 앱으로 복귀한다.
 */
export const requestSnsLoginViaRN = (provider: string, url: string) => {
  if (window.ReactNativeWebView) {
    window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'OPEN_SNS_LOGIN', provider, url }));
  }
};

/**
 * access 토큰 갱신을 RN 에 알린다 — 앱 저장소(AsyncStorage/SecureStore)도 새 토큰으로 바꾼다.
 * ⛔ 안 알리면 앱이 포그라운드로 돌아올 때 «로그인 당시의 옛 토큰» 을 다시 주입해
 *    refresh 토큰 수명이 지나면 강제 로그아웃된다(2026-09-27). 구버전 앱은 모르는 타입이라 무시한다.
 */
export const notifyTokenRefreshed = (accessToken: string, refreshToken?: string) => {
  if (window.ReactNativeWebView) {
    window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'TOKEN_REFRESHED', accessToken, refreshToken }));
  }
};

/**
 * 인앱 브라우저 시트로 연다(파일 다운로드·외부 페이지) — 새 앱(UA 표식)만 처리한다.
 * 호출부는 supportsSnsAuthSession() 으로 먼저 확인한다.
 */
export const openInAppBrowserViaRN = (url: string) => {
  if (window.ReactNativeWebView) {
    window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'OPEN_IN_APP_BROWSER', url }));
  }
};

/** refresh 토큰 만료를 RN에 알려 AsyncStorage 클리어 유도 */
export const notifyTokenExpired = () => {
  if (window.ReactNativeWebView) {
    window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'TOKEN_EXPIRED' }));
  }
};

/** 현재 Vue 앱이 RN WebView 환경인지 여부 */
export const isRunningInRNWebView = (): boolean => {
  return !!window.ReactNativeWebView;
};

/**
 * 현재 Vue 앱이 «iOS 앱»(RN WebView) 인지 여부.
 * ⚠️ iPadOS WebView 는 UA 가 `Macintosh` 로 나온다 — iPhone/iPad 만 보면 iPad 앱이 빠진다.
 *    RN WebView 안에서만 판정하므로 Mac 브라우저와 섞이지 않는다.
 */
export const isRunningInIosApp = (): boolean => {
  return isRunningInRNWebView() && /iPhone|iPad|iPod|Macintosh/i.test(navigator.userAgent);
};

/**
 * 이 앱이 SNS 로그인을 «인앱 시트»(openAuthSessionAsync)로 받을 수 있는가.
 * 새 앱은 WebView UA 에 `GCB-SNS-SESSION` 을 붙인다(WebViewScreen applicationNameForUserAgent).
 * ⛔ 이것이 true 일 때만 state=app_* 를 보낸다 — 서버가 golchongbi:// 로 돌려보내는데,
 *    표식 없는 구버전 앱(시스템 브라우저로 여는 방식)은 그 주소를 열 곳이 없어 로그인이 깨진다.
 */
export const supportsSnsAuthSession = (): boolean => {
  return isRunningInRNWebView() && /GCB-SNS-SESSION/.test(navigator.userAgent);
};

// ===== 인앱결제(IAP) 브릿지 — SSoT: docs/05_연동/IAP_상품ID_설계_20260705.md §10 =====

/**
 * App Store 월 구독(com.golchongbi.cash.14900) 판매가 — 대한민국 15,000원 (ASC 등록값, 2026-09-25 확인).
 * iOS 앱에서는 Apple 이 이 금액을 청구하므로 DB 요금(동호회 할인 반영가)이 아니라 이 값을 보여 준다.
 * ⚠️ ASC 에서 가격을 바꾸면 여기도 같이 바꾼다. 웹·PC·Android 는 DB 요금 그대로.
 */
export const IOS_APP_MONTHLY_PRICE = 15000;

/** RN → WebView 로 오는 IAP 구매 결과 (window 'message' 이벤트로 수신) */
export interface IapPurchaseResult {
  type: 'IAP_PURCHASE_RESULT';
  success: boolean;
  refKey: string | null;
  platform?: 'APPLE' | 'GOOGLE';
  productId?: string;
  storeTxId?: string;
  receipt?: string;
  code?: 'USER_CANCELED' | 'PENDING' | 'STORE_ERROR' | 'NOT_SUPPORTED' | 'UNKNOWN_PRODUCT' | 'NOT_FOUND';
  message?: string;
  /** 2026-10-03 «구매 복원»으로 다시 받은 구매(새 결제 아님) */
  restored?: boolean;
}

/**
 * 구매 복원(구독) 요청 — 2026-10-03 App Store 요건(TODO_iOS 심사 §3-2).
 * 결과는 새 결제와 같은 IAP_PURCHASE_RESULT(restored=true). 구버전 앱은 응답하지 않는다 → 화면의 대기 시간 제한이 푼다.
 */
export const requestIapRestore = () => {
  if (window.ReactNativeWebView) {
    window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'IAP_RESTORE_REQUEST' }))
  }
}

/**
 * 골총캐쉬 IAP 구매 요청 — refKey(내부키, 예: CASH_10K)만 전달.
 * 플랫폼별 productId 변환은 RN이 담당. 결과는 IAP_PURCHASE_RESULT message 이벤트로 수신.
 */
export const requestIapPurchase = (
  refKey: string,
  productKind: 'CONSUMABLE' | 'SUBSCRIPTION' = 'CONSUMABLE',
  appAccountToken?: string,
) => {
  if (window.ReactNativeWebView) {
    // appAccountToken — 서버가 발급한 회원 토큰. 앱이 구매에 실어 보내면 검증 때 «이 회원의 구매» 인지 대조한다.
    // 구버전 앱은 이 값을 무시한다(서버는 토큰 없는 구매도 받되 기록한다).
    window.ReactNativeWebView.postMessage(
      JSON.stringify({ type: 'IAP_PURCHASE_REQUEST', refKey, productKind, appAccountToken })
    );
  }
};

/**
 * IAP 트랜잭션 종결 요청 — 반드시 서버 영수증 검증·적립(iap-verify) 성공 후 호출.
 * 미호출 시 Android 3일 후 자동환불 / iOS 트랜잭션 재전달.
 */
export const finishIapPurchase = (storeTxId: string) => {
  if (window.ReactNativeWebView) {
    window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'IAP_FINISH', storeTxId }));
  }
};

/**
 * 인앱결제 충전분의 스토어 환불 신청 페이지 (2026-10-15).
 * 약관상 인앱 충전분은 회사가 환불할 수 없고 스토어 정책을 따른다 — 회원이 갈 곳은 스토어뿐이다.
 * 스토어에서 환불되면 웹훅(IapApiController)이 해당 충전분을 회수한다.
 */
export const STORE_REFUND_URL = {
  APPLE: 'https://reportaproblem.apple.com',
  GOOGLE: 'https://play.google.com/store/account/orderhistory',
} as const

/** 이 앱이 설치된 기기의 스토어 — iOS 앱이면 APPLE, 그 밖의 앱은 GOOGLE */
export const currentAppStore = (): 'APPLE' | 'GOOGLE' => (isRunningInIosApp() ? 'APPLE' : 'GOOGLE')

/**
 * 스토어 환불 페이지를 인앱 브라우저 시트(SFSafariViewController / Chrome Custom Tabs)로 연다.
 * ⛔ window.open 은 쓰지 않는다 — 앱이 결제용 모달 WebView 로 열고, Google 은 WebView 안 로그인을 막는다.
 *    새 앱은 OPEN_IN_APP_BROWSER, 구버전 앱은 최상위 이동 → 앱의 외부 링크 처리(handleShouldStartLoad)가 받는다.
 */
export const openStoreRefundPage = (store: 'APPLE' | 'GOOGLE') => {
  const url = STORE_REFUND_URL[store]
  if (supportsSnsAuthSession()) {
    openInAppBrowserViaRN(url)
  } else {
    window.location.href = url
  }
}
